import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getIndirectTaxFilingsV2Status,
  triggerIndirectTaxFilingsV2AggregateSync,
  triggerIndirectTaxFilingsV2BreakdownGeneration,
  triggerIndirectTaxFilingsV2ContributionSync,
} from '@commenda/api/indirect-tax/filings';
import { IIndirectTaxFilingV2StatusResponse } from '@commenda/api/indirect-tax/filings/types';

import { useNotification } from '../../../hooks/useNotification';
import { IndirectTaxFilingAccessHelper } from '../../common/helpers/IndirectTaxFilingAccessHelper';
import { useIndirectTaxFilingAccess } from '../../common/hooks/useIndirectTaxFilingAccess';
import { IndirectTaxRegion } from '../../common/types';
import {
  IndirectTaxFilingsStatusBreakdownDisplayRow,
  IndirectTaxFilingsStatusContributionDisplayRow,
  IndirectTaxFilingsStatusHelper,
} from '../helpers';
import { IIndirectTaxFiling } from '../types';

export type {
  IndirectTaxFilingsStatusBreakdownDisplayRow,
  IndirectTaxFilingsStatusContributionDisplayRow,
};

interface UseIndirectTaxFilingsStatusModalParams {
  isOpen: boolean;
  corporationId: number;
  filings: ReadonlyArray<IIndirectTaxFiling>;
}

interface UseIndirectTaxFilingsStatusModalResult {
  isLoading: boolean;
  error: string | null;
  hasBreakdownFilings: boolean;
  hasContributionFilings: boolean;
  hasAnyFilings: boolean;
  breakdownFilingCount: number;
  contributionFilingCount: number;
  breakdownDisplayRows: IndirectTaxFilingsStatusBreakdownDisplayRow[];
  contributionDisplayRows: IndirectTaxFilingsStatusContributionDisplayRow[];
  refetch: () => Promise<void>;
  /**
   * Per-banner-action callbacks. Each one fires the underlying logical-
   * backend trigger against the in-scope filing IDs for its tab, then
   * auto-refetches so the verdict transitions without a second click.
   */
  isBreakdownTriggering: boolean;
  isContributionTriggering: boolean;
  isAggregateSyncTriggering: boolean;
  triggerBreakdownGeneration: () => Promise<void>;
  triggerContributionSync: () => Promise<void>;
  /**
   * US/CA-only re-run of the per-filing aggregate (filing-numbers) sync.
   * Fire-and-forget — no job_id is surfaced; caller refetches status to
   * see breakdown counters move once the background work catches up.
   */
  triggerAggregateSync: () => Promise<void>;
  triggerError: string | null;
}

const useIndirectTaxFilingsStatusModal = ({
  isOpen,
  corporationId,
  filings,
}: UseIndirectTaxFilingsStatusModalParams): UseIndirectTaxFilingsStatusModalResult => {
  const { isInternalUser } = useIndirectTaxFilingAccess(
    IndirectTaxRegion.ALL_REGIONS
  );
  const [statusResponse, setStatusResponse] =
    useState<IIndirectTaxFilingV2StatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBreakdownTriggering, setIsBreakdownTriggering] = useState(false);
  const [isContributionTriggering, setIsContributionTriggering] =
    useState(false);
  const [isAggregateSyncTriggering, setIsAggregateSyncTriggering] =
    useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);

  const { setNotification } = useNotification();
  const notifySuccess = useCallback(
    (title: string) => {
      setNotification({ show: true, valid: true, title });
    },
    [setNotification]
  );
  const notifyError = useCallback(
    (title: string) => {
      setNotification({ show: true, valid: false, title });
    },
    [setNotification]
  );

  const filingSplit = useMemo(
    () =>
      IndirectTaxFilingsStatusHelper.splitFilingsByRegion(
        filings.filter((filing) =>
          IndirectTaxFilingAccessHelper.canViewDetails(
            filing.region,
            isInternalUser
          )
        )
      ),
    [filings, isInternalUser]
  );

  const {
    breakdownFilingIds,
    aggregateSyncFilingIds,
    breakdownContributionFilingIds,
    contributionFilingIds,
    filingsById,
  } = filingSplit;
  const hasBreakdownFilings = breakdownFilingIds.length > 0;
  const hasContributionFilings = contributionFilingIds.length > 0;
  const hasAnyFilings = hasBreakdownFilings || hasContributionFilings;

  const refetch = useCallback(async () => {
    if (!hasAnyFilings) {
      setStatusResponse(null);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    const result = await getIndirectTaxFilingsV2Status({
      params: {
        corporationId,
        breakdownFilingIds: hasBreakdownFilings
          ? breakdownFilingIds
          : undefined,
        contributionFilingIds: hasContributionFilings
          ? contributionFilingIds
          : undefined,
      },
    });
    if (result.success && result.data) {
      setStatusResponse(result.data);
    } else {
      setError(result.error || 'Failed to fetch filings status');
    }
    setIsLoading(false);
  }, [
    corporationId,
    hasAnyFilings,
    hasBreakdownFilings,
    hasContributionFilings,
    breakdownFilingIds,
    contributionFilingIds,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setStatusResponse(null);
      setError(null);
      return;
    }
    void refetch();
  }, [isOpen, refetch]);

  const breakdownDisplayRows = useMemo(
    () =>
      IndirectTaxFilingsStatusHelper.buildBreakdownDisplayRows(
        statusResponse,
        filingsById
      ),
    [statusResponse, filingsById]
  );

  const contributionDisplayRows = useMemo(
    () =>
      IndirectTaxFilingsStatusHelper.buildContributionDisplayRows(
        statusResponse,
        contributionFilingIds,
        filingsById
      ),
    [statusResponse, contributionFilingIds, filingsById]
  );

  const triggerBreakdownGeneration = useCallback(async () => {
    if (breakdownFilingIds.length === 0) return;
    setIsBreakdownTriggering(true);
    setTriggerError(null);
    const result = await triggerIndirectTaxFilingsV2BreakdownGeneration({
      params: { corporationId, filingIds: breakdownFilingIds },
    });
    setIsBreakdownTriggering(false);
    if (!result.success) {
      const message = result.error || 'Failed to trigger breakdown generation';
      setTriggerError(message);
      notifyError(message);
      return;
    }
    notifySuccess(result.data?.message || 'Tax-breakdown generation queued.');
    await refetch();
  }, [corporationId, breakdownFilingIds, refetch, notifySuccess, notifyError]);

  const runContributionSync = useCallback(
    async (filingIds: string[]): Promise<boolean> => {
      const result = await triggerIndirectTaxFilingsV2ContributionSync({
        params: { corporationId, filingIds },
      });
      if (!result.success) {
        const message = result.error || 'Failed to trigger contribution sync';
        setTriggerError(message);
        notifyError(message);
        return false;
      }
      const { successCount, failureCount, skippedCount } = result.data ?? {
        successCount: 0,
        failureCount: 0,
        skippedCount: 0,
      };
      const summary = [
        `${successCount} succeeded`,
        failureCount > 0 ? `${failureCount} failed` : null,
        skippedCount > 0 ? `${skippedCount} skipped` : null,
      ]
        .filter(Boolean)
        .join(', ');
      if (failureCount > 0) {
        notifyError(`Contribution sync queued — ${summary}.`);
      } else {
        notifySuccess(`Contribution sync queued — ${summary}.`);
      }
      return true;
    },
    [corporationId, notifySuccess, notifyError]
  );

  const runAggregateSync = useCallback(
    async (filingIds: string[]): Promise<boolean> => {
      const result = await triggerIndirectTaxFilingsV2AggregateSync({
        params: { corporationId, filingIds },
      });
      if (!result.success) {
        const message = result.error || 'Failed to trigger aggregate sync';
        setTriggerError(message);
        notifyError(message);
        return false;
      }
      notifySuccess(result.data?.message || 'Filing-aggregates sync queued.');
      return true;
    },
    [corporationId, notifySuccess, notifyError]
  );

  // Second stage for the breakdown tab: the job that turns breakdowns into
  // filing numbers differs per pipeline (aggregate job vs contribution
  // engine), so each filing is routed to the one its region uses.
  const triggerAggregateSync = useCallback(async () => {
    if (breakdownFilingIds.length === 0) return;
    setIsAggregateSyncTriggering(true);
    setTriggerError(null);
    const results = await Promise.all([
      aggregateSyncFilingIds.length > 0
        ? runAggregateSync(aggregateSyncFilingIds)
        : Promise.resolve(true),
      breakdownContributionFilingIds.length > 0
        ? runContributionSync(breakdownContributionFilingIds)
        : Promise.resolve(true),
    ]);
    setIsAggregateSyncTriggering(false);
    if (results.every(Boolean)) {
      await refetch();
    }
  }, [
    breakdownFilingIds,
    aggregateSyncFilingIds,
    breakdownContributionFilingIds,
    runAggregateSync,
    runContributionSync,
    refetch,
  ]);

  const triggerContributionSync = useCallback(async () => {
    if (contributionFilingIds.length === 0) return;
    setIsContributionTriggering(true);
    setTriggerError(null);
    const succeeded = await runContributionSync(contributionFilingIds);
    setIsContributionTriggering(false);
    if (succeeded) {
      await refetch();
    }
  }, [contributionFilingIds, runContributionSync, refetch]);

  return {
    isLoading,
    error,
    hasBreakdownFilings,
    hasContributionFilings,
    hasAnyFilings,
    breakdownFilingCount: breakdownFilingIds.length,
    contributionFilingCount: contributionFilingIds.length,
    breakdownDisplayRows,
    contributionDisplayRows,
    refetch,
    isBreakdownTriggering,
    isContributionTriggering,
    isAggregateSyncTriggering,
    triggerBreakdownGeneration,
    triggerContributionSync,
    triggerAggregateSync,
    triggerError,
  };
};

export { useIndirectTaxFilingsStatusModal };
